// Story 8.1 (ADO-2010), Task 7.2 / A11.1: one "Your open carts" card as an accordion
// (Figma 5031:4601; collapsed 5031:4605 VC account and 5031:4607 single account,
// expanded 5031:4608).
//
// One card per cart: a depot and, Story 8.4 (ADO-2013), a Trading Account, so each Value
// Centre account has its own card, titled by the centre over its account line.
//
// The head row (depot edge, icon, title, optional detail, line count) toggles the
// card: its one control is the round Toggle `button` (`aria-expanded` + `aria-controls`),
// whose hit area is stretched over the whole row in CSS. The design system has no
// Accordion or Disclosure primitive (the PDP's `ProductDetail/Accordion` takes a plain
// string title and draws other chrome), so the disclosure is local, following the same
// a11y contract. Its panel opens and closes through the design-system `Collapse` (ADO-2262).
//
// Opening the card shows its lines ("Items": name, quantity and price, the first five, then
// "+N more", and the cart's total value), read on demand by the page and cached for the
// visit; the page owns the read state so a card never loses it when the mobile sheet
// closes. A failed read shows a retry inside this card only. Then the actions: "Continue
// shopping" takes the cart up and goes where the choice screen sends a take-up (the
// account's destination, or the location the browsing gate carried); "Review order"
// (Figma 5031:4608 reads "Update order"; the PO renamed it, 2026-10-05) takes it up and
// opens the depot's Order Review (Story 8.11, `/cart`); "Submit now" (Story 8.6, ADO
// #2015) takes it up and sends it at once, asking for nothing (no review step, no order
// reference, no fulfilment options; PO, 2026-10-05). The page then confirms a sent order in
// its "Order sent" dialog, or opens the Order Review to show why the send failed. The page
// owns whether a send is running (`sending` for this cart, `locked` for any): while one
// runs every card's actions are disabled and this button reads
// "Submitting…". A cart that could not be opened (nothing sent) says so in this card, with
// a retry; the page holds that message too (`submitFailed`), so it is still there when the
// mobile sheet is closed and reopened. A send the session moved away from returns the card
// to idle without a message.
// Story 8.4 (ADO-2013), D-4: the card takes up exactly its own cart (its depot and account), so
// it asks for no account: the list already shows each account's cart separately. Enabler E8-G
// (ADO-3109): its entry is the one of the cart's own kind (DA-8: Chill's and Fruit & Veg's carts
// are two cards), and a staff user's card (DA-10: a staff entry offers no accounts) names the
// store account the cart is keyed by and takes the cart up for it.
//
// Story 8.5 (ADO-2014), D-20: the cart's cut-off pill (`small`), from the shared read (for the
// cart's own account, Story 8.4). In
// the desktop column it sits in the head row before the Toggle; in the tablet tray and the
// mobile sheet it sits on the "N lines" row, right-aligned under the Toggle
// (`cutOffPlacement`, laid out in CSS). It stays when the card is expanded, and is not part
// of the Toggle's name (it ticks every second).
//
// Money: the PRICE column is each line's value (`lineValue`, the locked line total, else
// unit price times quantity) and the Total is the cart's value (`cartValue`), from the
// prices the server locked on the lines; a line with no locked price reads as
// UNKNOWN_TOTAL, and so does a total holding one. The head shows the cart's lines and
// value as the host's one snapshot of it gives them (`cart` and `value`, from the page's
// `cartAt`); with no value it shows its lines alone.
//
// Deliberately absent: any case count.

import { useId } from 'react';
import { Banner, Button, Collapse, CutOffPill, Icon } from '../../design-system';
import type { BasketLine } from '../../api/basket.client';
import type { OpenCart, OrderingAccount, OrderingEntry } from '../../api/ordering-choices.client';
import { isNoLongerListed, PRODUCT_NO_LONGER_LISTED } from '../../basket/CartLineRow';
import { cartValue, linesLabel, UNKNOWN_TOTAL } from '../../basket/cartTotals';
import { lineValue } from '../../basket/cartMoney';
import { formatMoney, type MoneyValue } from '../../shared/money';
import { useDepotCutOff } from '../../basket/useDepotCutOff';
import { isCutOffShown } from '../../shared/cutOffDisplay';
import { accountLine, namesItsAccount } from '../../ordering/orderingChoices';
import { depotAccent, openCartIconSrc } from './depotAccent';
import styles from './ChoosePage.module.css';

/** The lines shown before "+N more" (Figma 5031:4608). */
export const OPEN_CART_VISIBLE_LINES = 5;

/** A cart's on-demand line read, owned by the page. */
export type OpenCartLines =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'loaded'; readonly lines: readonly BasketLine[] };

/** The head's title and detail line: the entry's label, over the account line ("<account
 *  name> · <number>") where the entry names its account (`namesItsAccount`: every Value Centre
 *  account, and a core depot's only when it offers several; 5031:4605, 5031:4607). */
function heading(
  entry: OrderingEntry,
  account: OrderingAccount | null,
): { title: string; detail: string | null } {
  return {
    title: entry.label,
    detail: account !== null && namesItsAccount(entry) ? accountLine(account) : null,
  };
}

/** A money figure, or UNKNOWN_TOTAL when it is not known (never a false €0.00). */
function moneyOrUnknown(value: MoneyValue | null): string {
  return value === null ? UNKNOWN_TOTAL : formatMoney(value);
}

function CartItems({ lines, onRetry }: { lines: OpenCartLines; onRetry: () => void }) {
  if (lines.status === 'loading') {
    return (
      <p role="status" className={styles.cartItemsNote}>
        Loading this cart…
      </p>
    );
  }
  if (lines.status === 'error') {
    return (
      <div className={styles.cartItemsError}>
        <Banner type="error">We couldn&rsquo;t load this cart.</Banner>
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  if (lines.lines.length === 0) {
    return <p className={styles.cartItemsNote}>This cart is now empty.</p>;
  }
  const shown = lines.lines.slice(0, OPEN_CART_VISIBLE_LINES);
  const more = lines.lines.length - shown.length;
  const total = cartValue(lines.lines);
  return (
    <>
      <table className={styles.cartItemsTable}>
        <thead>
          <tr>
            <th scope="col" className={styles.cartItemName}>
              Item
            </th>
            <th scope="col" className={styles.cartItemQty}>
              Qty
            </th>
            <th scope="col" className={styles.cartItemPrice}>
              Price
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((line, index) => (
            <tr key={`${line.skuCode}-${index}`}>
              <td className={styles.cartItemName}>
                {line.name ? (
                  line.name
                ) : (
                  <>
                    {line.skuCode}
                    {isNoLongerListed(line) && (
                      <span className={styles.cartItemNotListed}>{PRODUCT_NO_LONGER_LISTED}</span>
                    )}
                  </>
                )}
              </td>
              <td className={styles.cartItemQty}>{line.quantity}</td>
              <td className={styles.cartItemPrice}>{moneyOrUnknown(lineValue(line))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {more > 0 && <p className={styles.cartItemsMore}>+{more} more</p>}
      {/* The Figma "Total" is the cart value; the choice screen shows no case count. */}
      <p className={styles.cartItemsTotal} data-testid="open-cart-total">
        <span className={styles.cartItemsTotalLabel}>Total</span>
        <span className={styles.cartItemsTotalValue}>{moneyOrUnknown(total)}</span>
      </p>
    </>
  );
}

export interface OpenCartCardProps {
  /** The cart: its depot and Trading Account, and its counts. */
  readonly cart: Pick<OpenCart, 'depotId' | 'customerAccountCode' | 'caseCount' | 'lineCount'>;
  /** The entry that takes the cart up (`entryForCart`). */
  readonly entry: OrderingEntry;
  readonly expanded: boolean;
  /** The page's line read for this cart, `undefined` before the first opening. */
  readonly lines: OpenCartLines | undefined;
  /** The cart's value where the page knows it, shown in the head; null otherwise. */
  readonly value: MoneyValue | null;
  /** Where the cut-off pill sits: `head`, beside the Toggle (the desktop column), or
   *  `meta`, on the lines row under the Toggle (the tablet tray and mobile sheet). */
  readonly cutOffPlacement: 'head' | 'meta';
  readonly onToggle: () => void;
  readonly onRetry: () => void;
  readonly onContinueShopping: (entry: OrderingEntry, customerAccountCode: string | null) => void;
  readonly onReviewOrder: (entry: OrderingEntry, customerAccountCode: string | null) => void;
  /** True while this cart's "Submit now" send runs. */
  readonly sending: boolean;
  /** True while any "Submit now" send on the page runs: every action waits for it. */
  readonly locked: boolean;
  /** True when this cart's last "Submit now" could not open it (nothing was sent). */
  readonly submitFailed: boolean;
  /** Take the cart up and send it. */
  readonly onSubmitNow: (entry: OrderingEntry, customerAccountCode: string | null) => void;
}

export function OpenCartCard({
  cart,
  entry,
  expanded,
  lines,
  value,
  cutOffPlacement,
  onToggle,
  onRetry,
  onContinueShopping,
  onReviewOrder,
  sending,
  locked,
  submitFailed,
  onSubmitNow,
}: OpenCartCardProps) {
  const panelId = useId();
  const cutOff = useDepotCutOff({
    depotId: cart.depotId,
    customerAccountCode: cart.customerAccountCode,
  });
  const pill = isCutOffShown(cutOff.status, cutOff.countdown) ? (
    <span className={styles.cartCutOff}>
      <CutOffPill {...cutOff} />
    </span>
  ) : null;
  const account =
    entry.accounts.find(
      (candidate) => candidate.customerAccountCode === cart.customerAccountCode,
    ) ?? null;
  // A staff entry offers no accounts: the cart's own account is the store it was picked for.
  const staffCart = entry.accounts.length === 0;
  const { title, detail } = staffCart
    ? { title: entry.label, detail: cart.customerAccountCode }
    : heading(entry, account);
  const meta = linesLabel(cart.lineCount);
  const money = value === null ? null : formatMoney(value);
  const accent = depotAccent(entry);
  const iconSrc = openCartIconSrc(accent);
  const ready = staffCart ? cart.customerAccountCode !== null : account !== null;
  const accountCode = staffCart ? cart.customerAccountCode : (account?.customerAccountCode ?? null);
  const actionsDisabled = !ready || locked;

  const submitNow = () => onSubmitNow(entry, accountCode);

  return (
    <li>
      <div className={styles.cartCard} data-accent={accent} data-open={expanded}>
        <span className={styles.cartEdge} aria-hidden />
        <div className={styles.cartHead} data-cut-off-placement={cutOffPlacement}>
          <span className={styles.cartIcon} aria-hidden>
            {iconSrc === null ? (
              <Icon name="cart" size={20} />
            ) : (
              <img src={iconSrc} width={20} height={20} alt="" />
            )}
          </span>
          <span className={styles.cartInfo}>
            <span className={styles.cartTitle}>{title}</span>
            {detail !== null && <span className={styles.cartDetail}>{detail}</span>}
            <span className={styles.cartMeta}>
              {meta}
              {money !== null && (
                <>
                  {' · '}
                  <span className={styles.cartMetaValue}>{money}</span>
                </>
              )}
            </span>
          </span>
          {pill}
          <button
            type="button"
            className={styles.cartToggle}
            aria-expanded={expanded}
            aria-controls={panelId}
            aria-label={[title, detail, money === null ? meta : `${meta} · ${money}`]
              .filter(Boolean)
              .join(', ')}
            onClick={onToggle}
          >
            <span className={styles.cartToggleIcon} data-open={expanded} aria-hidden>
              <Icon name="caretDown" size={16} />
            </span>
          </button>
        </div>
        {/* ADO-2262: the lines and actions open and close through `Collapse` (they stay
            mounted, inert while closed). */}
        <Collapse id={panelId} open={expanded}>
          <div className={styles.cartItems}>
            {lines !== undefined && <CartItems lines={lines} onRetry={onRetry} />}
          </div>
          <div className={styles.cartActions}>
            <button
              type="button"
              className={styles.cartContinue}
              disabled={actionsDisabled}
              onClick={() => onContinueShopping(entry, accountCode)}
            >
              Continue shopping
              <Icon name="arrowRight" size={14} />
            </button>
            <Button
              variant="secondary"
              fullWidth
              disabled={actionsDisabled}
              onClick={() => onReviewOrder(entry, accountCode)}
            >
              Review order
            </Button>
            <Button variant="primary" fullWidth disabled={actionsDisabled} onClick={submitNow}>
              {sending ? 'Submitting…' : 'Submit now'}
            </Button>
            {submitFailed && (
              <div className={styles.cartItemsError}>
                <Banner type="error">We couldn&rsquo;t open this cart, so it was not sent.</Banner>
                <Button variant="secondary" disabled={actionsDisabled} onClick={submitNow}>
                  Try again
                </Button>
              </div>
            )}
          </div>
        </Collapse>
      </div>
    </li>
  );
}
